/**
 * @voltxampere/presets — Op-amp non-inverting amplifier (#10)
 *
 * V_in to in_pos directly, feedback divider R1 (out → in_neg) and
 * R2 (in_neg → gnd). Closed-loop gain Av = 1 + R1/R2. With
 * R1 = R2 = 10 kΩ, Av = 2 → V(out) = 2 V for V_in = 1 V.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const opampNonInverting = {
    id: "opamp-non-inverting",
    name: { tr: "Op-Amp Tersleyici Olmayan", en: "Op-Amp Non-Inverting" },
    category: "amplifier",
    difficulty: 3,
    description: {
        tr: "Tersleyici olmayan op-amp yükselteç. R1=R2=10kΩ → Av = 1 + R1/R2 = 2.",
        en: "Non-inverting op-amp amplifier. R1=R2=10kΩ → Av = 1 + R1/R2 = 2.",
    },
    // Sprint 25 R2 lane 4D-A — hand-authored layout. V1 left, U1
    // body centered at (60,0). R1 (feedback resistor, out → fb)
    // horizontal above the op-amp at y=-100 (rot 180 so p1=out is on
    // the right at x=80). R2 (gnd divider, fb → gnd) vertical to the
    // left of the op-amp at x=-40 (rot 90 so p1=fb is at the top,
    // p2=gnd at the bottom). in_pos approached via y=-40 entering at
    // x=20 then horizontal to (40,4) to dodge the in_neg vertical
    // feedback rail at x=40.
    circuit: makeCircuit({
        slot: 10,
        name: "Op-Amp Non-Inverting",
        description: "Closed-loop gain Av = 2",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "fb" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 1 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -280, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "U1",
                type: "opamp",
                params: {},
                terminals: { in_pos: "net_in", in_neg: "net_fb", out: "net_out" },
                label: "U1",
                position: { x: 60, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_out", p2: "net_fb" },
                label: "R1",
                position: { x: 60, y: -100 },
                rotation: 180,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_fb", p2: "net_gnd" },
                label: "R2",
                position: { x: -40, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -280, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // net_in: V1.pos (-280,-10) → in_pos (40,4). Goes up and over
            // via y=-40, drops down at x=20, then steps right to (40,4).
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -280, y: -10 },
                    { x: -280, y: -40 },
                    { x: 20, y: -40 },
                    { x: 20, y: 4 },
                    { x: 40, y: 4 },
                ],
                style: "orthogonal",
            },
            // net_out: U1.out (100,0) → R1.p1 (80,-100). Vertical at x=100
            // up to y=-100, then horizontal back to (80,-100).
            {
                id: "w_out",
                netId: "net_out",
                path: [
                    { x: 100, y: 0 },
                    { x: 100, y: -100 },
                    { x: 80, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_fb leg A: in_neg (40,-4) → R1.p2 (40,-100). Vertical
            // feedback drop at x=40.
            {
                id: "w_fb_r1",
                netId: "net_fb",
                path: [
                    { x: 40, y: -4 },
                    { x: 40, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_fb leg B: in_neg (40,-4) → R2.p1 (-40,-20). Horizontal
            // hop left at y=-4, then drop to y=-20 at x=-40.
            {
                id: "w_fb_r2",
                netId: "net_fb",
                path: [
                    { x: 40, y: -4 },
                    { x: -40, y: -4 },
                    { x: -40, y: -20 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg A: V1.neg (-280,10) → GND1.p (-280,71).
            {
                id: "w_gnd_v1",
                netId: "net_gnd",
                path: [
                    { x: -280, y: 10 },
                    { x: -280, y: 71 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg B: R2.p2 (-40,20) → GND1.p (-280,71) via y=80
            // bus.
            {
                id: "w_gnd_r2",
                netId: "net_gnd",
                path: [
                    { x: -40, y: 20 },
                    { x: -40, y: 80 },
                    { x: -280, y: 80 },
                    { x: -280, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "out",
            analysis: "dc",
            value: 2,
            tolerance: 0.05,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Tersleyici olmayan kazanç Av = 1 + R1/R2 — virtual short prensibi.",
            en: "Non-inverting gain Av = 1 + R1/R2 — virtual short principle.",
        },
        {
            tr: "Yüksek giriş empedansı: in_pos doğrudan kaynaktan beslenir.",
            en: "High input impedance: in_pos driven directly from source.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 2"],
};
//# sourceMappingURL=opamp-non-inverting.js.map